/**
 * usePackagings Hook
 *
 * React Query hooks for packagings.
 * @see US-8.21 - Gestion des Conditionnements
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  packagingsService,
  type CreatePackagingInput,
  type UpdatePackagingInput,
} from '../services/packagings.service';

/**
 * Query key factory for packagings
 */
export const packagingKeys = {
  all: ['packagings'] as const,
  lists: () => [...packagingKeys.all, 'list'] as const,
  list: (includeInactive: boolean) => [...packagingKeys.lists(), { includeInactive }] as const,
  details: () => [...packagingKeys.all, 'detail'] as const,
  detail: (id: string) => [...packagingKeys.details(), id] as const,
  active: () => [...packagingKeys.all, 'active'] as const,
};

/**
 * Hook to fetch packagings list
 */
export function usePackagings(includeInactive = false) {
  return useQuery({
    queryKey: packagingKeys.list(includeInactive),
    queryFn: () => packagingsService.list(includeInactive),
    placeholderData: (prev) => prev,
  });
}

/**
 * Hook to fetch single packaging
 */
export function usePackaging(id: string | null) {
  return useQuery({
    queryKey: packagingKeys.detail(id!),
    queryFn: () => packagingsService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch active packagings for dropdowns
 */
export function useActivePackagings() {
  return useQuery({
    queryKey: packagingKeys.active(),
    queryFn: () => packagingsService.getActive(),
  });
}

/**
 * Hook to create a packaging
 */
export function useCreatePackaging() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePackagingInput) => packagingsService.create(data),
    onSuccess: (packaging) => {
      queryClient.invalidateQueries({ queryKey: packagingKeys.all });
      toast.success(`Conditionnement "${packaging.name}" cree avec succes`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la creation');
    },
  });
}

/**
 * Hook to update a packaging
 */
export function useUpdatePackaging() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePackagingInput }) =>
      packagingsService.update(id, data),
    onSuccess: (packaging) => {
      queryClient.invalidateQueries({ queryKey: packagingKeys.all });
      toast.success(`Conditionnement "${packaging.name}" mis a jour`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la mise a jour');
    },
  });
}

/**
 * Hook to deactivate a packaging
 */
export function useDeactivatePackaging() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => packagingsService.deactivate(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: packagingKeys.all });
      toast.success(result.message);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la desactivation');
    },
  });
}

/**
 * Hook to activate a packaging
 */
export function useActivatePackaging() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => packagingsService.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: packagingKeys.all });
      toast.success('Conditionnement reactive');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la reactivation');
    },
  });
}
